/**
 * THE PROVISIONER — the presence behind the requisition shelves. A named
 * character, not the host: dry, transactional, faintly conspiratorial. Has
 * clearly been down here longer than the broadcast and knows more than the
 * host admits — never says it outright. No exclamation points (permanent
 * constraint); never names the host.
 *
 * Pure presentation: one line per procurement visit, selected here from the
 * run context and rendered above the shelves (src/ui/menus.ts). Selection
 * never blocks or gates the purchase flow.
 */

export interface ProvisionerContext {
  /** act of the floor just cleared (procgen acts keep counting past 3) */
  act: number
  /** past the scheduled programming: a dedicated endless run, or a won campaign */
  overtime: boolean
  /** lifetime intakes processed (the persisted dossier) */
  lifetimeRuns: number
  /** no procurement visit on record for this install */
  firstVisitEver: boolean
  /** the quiet all-archives ending is on file — the participant knows what the show is */
  quietEndingSeen: boolean
  /** current index at/above tuning.story.provisionerHighScore */
  highScore: boolean
  /** lifetimeRuns at/above tuning.story.provisionerVeteranRuns */
  veteran: boolean
  /** every amenity is permanently in stock (residuals fully invested) */
  amenityStockFull: boolean
}

const FIRST = 'New stock, new face. Everything on these shelves is billed against your index. I only stack them.'

const ACT1: readonly string[] = [
  'Take your time. The floor below will not.',
  'Prices are set upstairs. Complaints go further upstairs. Nothing comes back down but stock.',
  'Buy or don’t. The shelves get restocked either way. So does the floor.'
]

const ACT2: readonly string[] = [
  'You’re deeper than most of my regulars get. I’d stock accordingly.',
  'The manifest says this depth doesn’t need a vendor. The manifest has never been down here.',
  'Watch your index. It’s the only number down here that’s really yours. Mostly yours.'
]

const ACT3: readonly string[] = [
  'This close to the Director, I keep the good shelf stocked. Nobody asks who for.',
  'I sold to everyone on the roster. All of them. You’d be surprised what floor fifteen buys.',
  'The host reads you the clauses. I read the storeroom ledger. The ledger is older than the show.'
]

const OVERTIME: readonly string[] = [
  'Past fifteen and still buying. My kind of arrangement.',
  'No schedule down here. Just stock, and whoever keeps arriving. You keep arriving.'
]

/** mixed into the depth pool once the dossier shows enough intakes */
const VETERAN: readonly string[] = [
  'You again. The shelves remember, even when the intake forms don’t.',
  'Same face, new designation. I don’t ask. Stock’s where it always is.'
]

/** mixed in once the quiet sign-off is on file */
const QUIET: readonly string[] = [
  'You read all eighteen. Then you know I shelve more than equipment. Take what you need.',
  'So you know what the show is. The shelves don’t change. Neither does the arrangement. Buy something.'
]

/** mixed in while the current index runs very high */
const HIGH_SCORE: readonly string[] = [
  'An index like that buys attention upstairs. Spend some of it down here before they notice.',
  'Big number. The last one who carried an index like that is on the roster now. Rank four helped. Briefly.'
]

/** mixed in once every amenity is permanently in stock */
const STOCK_FULL: readonly string[] = [
  'Full shelves. I have not seen full shelves since before the numbering system.',
  'The catalog is complete. Whatever happens next is between you and the floor.'
]

/**
 * Immediate reactions: these swap the visit line in place when the event
 * happens on the open panel (an amenity approval, a residual investment) —
 * the one moment the fixed-per-visit rule bends, because the Provisioner is
 * reacting to a transaction that just crossed the counter.
 */
export const AMENITY_BOUGHT: readonly string[] = [
  'A comfort item. I will not tell the host if you will not.',
  'Good choice. The previous buyer had no complaints. No time for them, either.',
  'Perks move faster than armor down here. They always have.'
]

/** first-ever permanent stock expansion — the arrangement changes */
export const STOCK_FIRST = 'Residuals, cleared and invested. Nobody has put anything back into this place since the show began. Noted.'

/** later stock expansions */
export const STOCK_EXPANDED: readonly string[] = [
  'Stock expanded, permanently. Upstairs will want to know where it came from. Upstairs will not be told.',
  'Another line in the catalog. The storeroom ledger grows. The show never asks about the ledger.'
]

/**
 * One line per visit. First visit ever is fixed; otherwise the depth pool
 * (act 1/2/3, overtime past the schedule) is enriched — not replaced — by any
 * special-condition pools in force, so familiarity accumulates rather than
 * flipping registers.
 */
export function provisionerLine(ctx: ProvisionerContext, pick: (pool: readonly string[]) => string): string {
  if (ctx.firstVisitEver) return FIRST
  const depth = ctx.overtime ? OVERTIME : ctx.act >= 3 ? ACT3 : ctx.act === 2 ? ACT2 : ACT1
  const pool = [...depth]
  if (ctx.veteran) pool.push(...VETERAN)
  if (ctx.quietEndingSeen) pool.push(...QUIET)
  if (ctx.highScore) pool.push(...HIGH_SCORE)
  if (ctx.amenityStockFull) pool.push(...STOCK_FULL)
  return pick(pool)
}
